import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Loader2, AlertOctagon, Radio, Users, Sparkles, PlayCircle, Eye, EyeOff, X } from 'lucide-react'
import MarketTicker from './MarketTicker'
import MarketHeader from './MarketHeader'
import RelativeStrength from './RelativeStrength'
import MarketContextPanel from './MarketContextPanel'
import OptionsMarketPanel from './OptionsMarketPanel'
import DilutionPanel from './DilutionPanel'
import CommitteeBrief from './CommitteeBrief'
import HouseView from './HouseView'
import PortfolioPanel from './PortfolioPanel'
import HoldingsPanel from './HoldingsPanel'
import NewsFeed from './NewsFeed'
import PressReleases from './PressReleases'
import VerdictPanel from './VerdictPanel'
import TradeBlotter from './TradeBlotter'
import LeverageSimulator from './LeverageSimulator'
import ScenarioPanel from './ScenarioPanel'
import CatalystCalendar from './CatalystCalendar'
import CommitteeGrid from './CommitteeGrid'
import DebatePanel from './DebatePanel'
import MoatPanel from './MoatPanel'
import ShortInterestPanel from './ShortInterestPanel'
import InsidersPanel from './InsidersPanel'
import BearCasePanel from './BearCasePanel'
import VisitorStats from './VisitorStats'
import { fetchDashboard, fetchCommittee, mergeCommittee, hasCommitteeAccess, fetchPortfolio, getRevealCode, setRevealCode, clearRevealCode, getRunCode, setRunCode, recordVisit, fetchVisitStats, fetchOfficialNews, fetchMarketContext, fetchOptionsMarket } from '../data/ondsDataSource'
import { computePortfolio } from '../utils/portfolio'
import { useDesk } from '../deskContext'

export default function OndsDashboard() {
  const desk = useDesk()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [committeeStatus, setCommitteeStatus] = useState('idle')
  const [committeeProgress, setCommitteeProgress] = useState(null)
  const [portfolio, setPortfolio] = useState(null)
  const [promptOpen, setPromptOpen] = useState(false)
  const [runPromptOpen, setRunPromptOpen] = useState(false)
  const [runError, setRunError] = useState('')
  const [pendingAction, setPendingAction] = useState(null)
  const [visits, setVisits] = useState(null)
  const [pressReleases, setPressReleases] = useState([])
  const [marketContext, setMarketContext] = useState(null)
  const [optionsMarket, setOptionsMarket] = useState(null)

  const unlocked = !!portfolio

  const loadCommittee = useCallback(async (base, { force = false, cachedOnly = false } = {}) => {
    if (!hasCommitteeAccess()) {
      setCommitteeStatus('idle')
      return
    }
    if (!cachedOnly) setCommitteeStatus('running')
    try {
      const committee = await fetchCommittee(base.quote.ticker, {
        force,
        cachedOnly,
        onStatus: (p) => { setCommitteeStatus('running'); setCommitteeProgress(p) },
      })
      if (committee && committee.verdict) {
        setData((prev) => mergeCommittee(prev ?? base, committee))
        setCommitteeStatus('live')
      } else {
        setCommitteeStatus('idle')
      }
    } catch (e) {
      if (e?.auth === 'run') {
        setRunError('Wrong run code.')
        setRunPromptOpen(true)
        setCommitteeStatus('idle')
        return
      }
      if (e?.retryAfter) {
        setRunError(`Too many tries. Wait ${e.retryAfter}s.`)
        setRunPromptOpen(true)
        setCommitteeStatus('idle')
        return
      }
      setCommitteeStatus(cachedOnly ? 'idle' : 'fallback')
    }
  }, [])

  const load = useCallback(async (runCommittee = false) => {
    setLoading(true)
    setError(null)
    try {
      const base = await fetchDashboard(desk.ticker)
      setData(base)
      setLoading(false)
      fetchPortfolio(getRevealCode(), desk.ticker).then(setPortfolio).catch(() => setPortfolio(null))
      loadCommittee(base, runCommittee ? { force: true } : { cachedOnly: true })
    } catch (e) {
      setError(e?.message ?? 'Failed to load committee data')
      setLoading(false)
    }
  }, [loadCommittee, desk.ticker])

  const requireRun = useCallback((action) => {
    if (getRunCode()) { action(); return }
    setPendingAction(() => action)
    setRunError('')
    setRunPromptOpen(true)
  }, [])

  const handleRefresh = useCallback(() => requireRun(() => load(true)), [requireRun, load])

  const runAnalysis = useCallback(() => requireRun(() => { if (data) loadCommittee(data, { force: true }) }), [requireRun, data, loadCommittee])

  const submitRun = useCallback((code) => {
    setRunCode(code)
    setRunError('')
    setRunPromptOpen(false)
    pendingAction?.()
  }, [pendingAction])

  const reveal = useCallback(async (code) => {
    const inputs = await fetchPortfolio(code, desk.ticker)
    if (!inputs) return false
    setRevealCode(code)
    setPortfolio(inputs)
    if (data) loadCommittee(data, { cachedOnly: true })
    return true
  }, [data, loadCommittee, desk.ticker])

  const hide = useCallback(() => {
    clearRevealCode()
    setPortfolio(null)
    if (data) loadCommittee(data, { cachedOnly: true })
  }, [data, loadCommittee])

  useEffect(() => { load() }, [load])

  useEffect(() => { recordVisit() }, [])

  useEffect(() => { fetchOfficialNews(desk.ticker).then(setPressReleases).catch(() => setPressReleases([])) }, [desk.ticker])

  useEffect(() => { fetchMarketContext(desk.ticker).then(setMarketContext).catch(() => setMarketContext(null)) }, [desk.ticker])

  useEffect(() => { fetchOptionsMarket(desk.ticker).then(setOptionsMarket).catch(() => setOptionsMarket(null)) }, [desk.ticker])

  useEffect(() => {
    if (unlocked) fetchVisitStats(getRevealCode()).then(setVisits).catch(() => setVisits(null))
    else setVisits(null)
  }, [unlocked])

  const metrics = useMemo(
    () => (data && portfolio ? computePortfolio(portfolio, data.quote) : null),
    [data, portfolio]
  )

  if (loading && !data) return <FullLoader ticker={desk.ticker} />
  if (error && !data) return <FullError message={error} onRetry={load} />

  return (
    <div>
      <MarketTicker headlines={data.headlines} />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-5 md:py-6 flex flex-col gap-5 md:gap-6">
        <div className="flex items-center justify-end gap-3 -mb-2">
          {unlocked && visits && <div className="flex-1"><VisitorStats visits={visits} /></div>}
          <PrivacyToggle unlocked={unlocked} open={promptOpen} onOpenChange={setPromptOpen} onReveal={reveal} onHide={hide} />
        </div>

        <MarketHeader
          quote={data.quote}
          asOf={data.asOf}
          source={data.source}
          onRefresh={handleRefresh}
          loading={loading || committeeStatus === 'running'}
          priceTarget={data.verdict?.priceTarget}
          committee={data.committee}
        />

        <CommitteeStatusBar status={committeeStatus} progress={committeeProgress} onRun={runAnalysis} />

        {data.quote?.peers?.length > 0 && (
          <div className="onds-fade-up"><RelativeStrength quote={data.quote} /></div>
        )}

        {marketContext && (
          <div className="onds-fade-up"><MarketContextPanel context={marketContext} /></div>
        )}

        {optionsMarket && (
          <div className="onds-fade-up"><OptionsMarketPanel options={optionsMarket} /></div>
        )}

        {data.quote?.dilution && (
          <div className="onds-fade-up"><DilutionPanel dilution={data.quote.dilution} /></div>
        )}

        {pressReleases.length > 0 && (
          <div className="onds-fade-up"><PressReleases items={pressReleases} /></div>
        )}

        <div className="onds-fade-up"><CommitteeBrief brief={data.brief} /></div>

        {data.holdings?.length > 0 && (
          <div className="onds-fade-up"><HoldingsPanel holdings={data.holdings} battery={desk.battery} /></div>
        )}

        <div className="onds-fade-up"><HouseView houseView={data.houseView} /></div>

        <div className="grid lg:grid-cols-5 gap-5 md:gap-6">
          <div className="lg:col-span-3 onds-fade-up"><PortfolioPanel metrics={metrics} locked={!unlocked} /></div>
          <div className="lg:col-span-2 onds-fade-up"><NewsFeed news={data.news} /></div>
        </div>

        <div className="onds-fade-up"><VerdictPanel verdict={data.verdict} /></div>

        {data.bearCase && <div className="onds-fade-up"><BearCasePanel bearCase={data.bearCase} /></div>}

        <div className="onds-fade-up"><TradeBlotter trades={data.tradeIdeas} locked={!unlocked} /></div>

        <div className="grid lg:grid-cols-2 gap-5 md:gap-6">
          <div className="onds-fade-up"><LeverageSimulator price={data.quote?.price ?? 0} leverage={data.leverage} portfolio={portfolio} locked={!unlocked} /></div>
          <div className="onds-fade-up"><ScenarioPanel scenarios={data.scenarios} portfolio={portfolio} locked={!unlocked} /></div>
        </div>

        <div className="onds-fade-up"><CatalystCalendar catalysts={data.catalysts} /></div>

        <div className="onds-fade-up"><CommitteeGrid committee={data.committee} /></div>

        <div className="onds-fade-up"><DebatePanel debate={data.debate} /></div>

        {data.moat && <div className="onds-fade-up"><MoatPanel moat={data.moat} /></div>}

        {data.shortInterest && <div className="onds-fade-up"><ShortInterestPanel shortInterest={data.shortInterest} /></div>}

        {data.insiders && <div className="onds-fade-up"><InsidersPanel insiders={data.insiders} /></div>}

        <footer className="text-center text-xs text-[var(--onds-text-dim)] py-4">
          {desk.ticker} Committee Desk · Research and simulation only, not financial advice.
        </footer>
      </div>

      {runPromptOpen && (
        <RunPrompt
          error={runError}
          onSubmit={submitRun}
          onClose={() => { setRunPromptOpen(false); setPendingAction(null) }}
        />
      )}
    </div>
  )
}

function RunPrompt({ error, onSubmit, onClose }) {
  const [code, setCode] = useState('')

  const submit = (e) => {
    e.preventDefault()
    if (code.trim()) onSubmit(code.trim())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl border p-5 shadow-2xl"
        style={{ borderColor: 'var(--onds-accent)', backgroundColor: 'var(--onds-surface)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold" style={{ color: 'var(--onds-accent)' }}>Run analysis code</span>
          <button type="button" onClick={onClose} className="text-[var(--onds-text-dim)]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-[var(--onds-text-dim)] mb-3">A fresh committee run requires the run code.</p>
        <input
          type="password"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Run code"
          className="w-full rounded-lg px-3 py-2 text-sm mb-2 outline-none border"
          style={{ backgroundColor: 'var(--onds-bg)', borderColor: error ? 'var(--onds-bear)' : 'var(--onds-border)', color: 'var(--onds-text)' }}
        />
        {error && <p className="text-[11px] mb-2" style={{ color: 'var(--onds-bear)' }}>{error}</p>}
        <button
          type="submit"
          disabled={!code.trim()}
          className="w-full py-2 rounded-lg font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--onds-accent)', color: '#0a0e17' }}
        >
          <PlayCircle className="h-4 w-4" />
          Run analysis
        </button>
      </form>
    </div>
  )
}

function CommitteeStatusBar({ status, progress, onRun }) {
  if (status === 'idle') return <CommitteeIdleBar onRun={onRun} />
  if (status === 'running') return <CommitteeProgressBar progress={progress} />

  const config = {
    live: {
      icon: <Radio className="h-4 w-4" />,
      color: 'var(--onds-bull)',
      title: 'Live committee verdict',
      detail: 'Verdict, debate and brief below reflect the latest committee run.',
    },
    fallback: {
      icon: <Users className="h-4 w-4" />,
      color: 'var(--onds-text-dim)',
      title: 'Showing simulated committee',
      detail: 'Live committee is unavailable right now — refresh to try again.',
    },
  }[status]

  if (!config) return null

  return (
    <div
      className="flex items-center gap-3 rounded-xl px-4 py-3 border"
      style={{ borderColor: config.color, backgroundColor: 'var(--onds-surface)' }}
    >
      <span style={{ color: config.color }}>{config.icon}</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold" style={{ color: config.color }}>{config.title}</p>
        <p className="text-xs text-[var(--onds-text-dim)] truncate">{config.detail}</p>
      </div>
    </div>
  )
}

function PrivacyToggle({ unlocked, open, onOpenChange, onReveal, onHide }) {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const ok = await onReveal(code.trim())
      if (ok) {
        onOpenChange(false)
        setCode('')
      } else {
        setError('Wrong code.')
      }
    } catch (err) {
      setError(err?.retryAfter ? `Too many tries. Wait ${err.retryAfter}s.` : 'Try again shortly.')
    } finally {
      setBusy(false)
    }
  }

  const Icon = unlocked ? Eye : EyeOff

  return (
    <div className="relative">
      <button
        onClick={() => (unlocked ? onHide() : onOpenChange(!open))}
        className="flex items-center justify-center p-2 rounded-lg border transition-colors"
        style={{
          borderColor: unlocked ? 'var(--onds-bull)' : 'var(--onds-border)',
          color: unlocked ? 'var(--onds-bull)' : 'var(--onds-text-dim)',
          backgroundColor: 'var(--onds-surface)',
        }}
        title={unlocked ? 'Hide sensitive numbers' : 'Reveal sensitive numbers'}
        aria-label={unlocked ? 'Hide sensitive numbers' : 'Reveal sensitive numbers'}
      >
        <Icon className="h-4 w-4" />
      </button>

      {open && !unlocked && (
        <form
          onSubmit={submit}
          className="absolute right-0 top-full mt-2 z-20 w-64 rounded-xl border p-3 shadow-xl"
          style={{ borderColor: 'var(--onds-border)', backgroundColor: 'var(--onds-surface)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold">Enter access code</span>
            <button type="button" onClick={() => onOpenChange(false)} className="text-[var(--onds-text-dim)]">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <input
            type="password"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Access code"
            className="w-full rounded-lg px-3 py-2 text-sm mb-2 outline-none border"
            style={{ backgroundColor: 'var(--onds-bg)', borderColor: error ? 'var(--onds-bear)' : 'var(--onds-border)', color: 'var(--onds-text)' }}
          />
          {error && <p className="text-[11px] mb-2" style={{ color: 'var(--onds-bear)' }}>{error}</p>}
          <button
            type="submit"
            disabled={busy || !code.trim()}
            className="w-full py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            style={{ backgroundColor: 'var(--onds-accent)', color: '#0a0e17' }}
          >
            {busy ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      )}
    </div>
  )
}

function CommitteeIdleBar({ onRun }) {
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl px-4 py-3 border"
      style={{ borderColor: 'var(--onds-accent)', backgroundColor: 'var(--onds-surface)' }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span style={{ color: 'var(--onds-accent)' }}><Sparkles className="h-4 w-4" /></span>
        <div className="min-w-0">
          <p className="text-sm font-semibold" style={{ color: 'var(--onds-accent)' }}>Committee is standing by</p>
          <p className="text-xs text-[var(--onds-text-dim)]">Press Run analysis to convene the committee. Sections below show the last run or simulated placeholders until then.</p>
        </div>
      </div>
      <button
        onClick={onRun}
        className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm shrink-0 whitespace-nowrap"
        style={{ backgroundColor: 'var(--onds-accent)', color: '#0a0e17' }}
      >
        <PlayCircle className="h-4 w-4" />
        Run analysis
      </button>
    </div>
  )
}

function CommitteeProgressBar({ progress }) {
  const backendPct = progress?.percent ?? 0
  const stage = progress?.stage || 'Working'
  const [displayPct, setDisplayPct] = useState(0)
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setDisplayPct((prev) => {
        const base = Math.max(prev, backendPct)
        const cap = Math.min(99, backendPct + 12)
        return Math.min(cap, base + 0.4)
      })
      setElapsed((e) => e + 1)
    }, 1000)
    return () => clearInterval(id)
  }, [backendPct])

  useEffect(() => {
    const server = progress?.elapsedSeconds ?? 0
    setElapsed((e) => (server > e ? server : e))
  }, [progress?.elapsedSeconds])

  const pct = Math.round(displayPct)
  const mm = Math.floor(elapsed / 60)
  const ss = String(elapsed % 60).padStart(2, '0')

  return (
    <div className="rounded-xl px-4 py-3 border" style={{ borderColor: 'var(--onds-accent)', backgroundColor: 'var(--onds-surface)' }}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" style={{ color: 'var(--onds-accent)' }} />
          <span className="text-sm font-semibold shrink-0" style={{ color: 'var(--onds-accent)' }}>Committee in session</span>
          <span className="text-xs text-[var(--onds-text-dim)] truncate">· {stage}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0 tabular-nums">
          <span className="text-sm font-bold" style={{ color: 'var(--onds-accent)' }}>{pct}%</span>
          <span className="text-xs text-[var(--onds-text-dim)]">{mm}:{ss}</span>
        </div>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--onds-border)' }}>
        <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${pct}%`, backgroundColor: 'var(--onds-accent)' }} />
      </div>
    </div>
  )
}

function FullLoader({ ticker }) {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="h-10 w-10 animate-spin" style={{ color: 'var(--onds-accent)' }} />
      <div className="text-center">
        <p className="font-semibold">Convening the committee...</p>
        <p className="text-sm text-[var(--onds-text-dim)]">Fetching macro, sector and {ticker} intelligence</p>
      </div>
    </div>
  )
}

function FullError({ message, onRetry }) {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4">
      <AlertOctagon className="h-10 w-10" style={{ color: 'var(--onds-bear)' }} />
      <p className="font-semibold">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded-lg font-semibold"
        style={{ backgroundColor: 'var(--onds-accent)', color: '#0a0e17' }}
      >
        Retry
      </button>
    </div>
  )
}
